// v2/api/api-client.ts — API client wrapping existing Axios instance

import axiosInstance from 'config/axiosConfig';
import { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { ApiResponse, ApiError } from '../types/api';
import { isRefreshableAuthFailure } from '../../../shared/auth-failure';

// ─── Error Parsing ─────────────────────────────────────────────────────────

/**
 * Extracts error message from an AxiosError.
 *
 * LOBS (backend proxy) returns error responses as plain text with Content-Type: text/plain.
 * Axios sets error.response.data to the raw string in this case.
 *
 * If LOBS changes to return JSON errors in the future, update the object check below
 * to match the new shape (e.g. { message: string, code: string }).
 */
function parseApiError(error: AxiosError): ApiError {
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    // Backend returns plain text error messages (Content-Type: text/plain)
    if (typeof data === 'string' && data.trim()) {
      return { status, message: data.trim() };
    }

    // JSON response body (if backend changes to return JSON errors in the future)
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const msg = (obj.message || obj.error || obj.errorMessage) as string | undefined;
      if (typeof msg === 'string' && msg.trim()) {
        return { status, message: msg.trim(), code: (obj.code as string) || undefined };
      }
    }

    return { status, message: `Server error (${status})` };
  }

  if (error.request) {
    return { status: 0, message: 'Network error — no response received' };
  }

  return { status: 0, message: error.message || 'An unexpected error occurred' };
}

// ─── Token Refresh Logic ───────────────────────────────────────────────────

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void): void {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

async function attemptTokenRefresh(): Promise<string | null> {
  try {
    // Dynamic import to avoid circular dependency with AuthContext
    const { CognitoIdentityProviderClient, InitiateAuthCommand } = await import(
      '@aws-sdk/client-cognito-identity-provider'
    );

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    const cognitoClient = new CognitoIdentityProviderClient({
      region: process.env.REACT_APP_REGION || 'ap-south-1',
    });

    const command = new InitiateAuthCommand({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId:
        process.env.REACT_APP_COGNITO_CLIENT_ID || 'r3a1kq4e4qp726c8fprl9fp0b',
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    });

    const { AuthenticationResult } = await cognitoClient.send(command);

    if (AuthenticationResult?.AccessToken) {
      localStorage.setItem('accessToken', AuthenticationResult.AccessToken);
      return AuthenticationResult.AccessToken;
    }

    return null;
  } catch {
    return null;
  }
}

// ─── Response Interceptor ──────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Attempt refresh on an expired-token 401/403, once per request. The _retry
    // flag is what stops a refresh loop: if the fresh token is rejected too, we
    // fall through and surface the error.
    if (
      isRefreshableAuthFailure(error.response?.status, error.response?.data) &&
      originalRequest &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        // Queue this request until the token is refreshed
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers['x-access-token'] = token;
            }
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const newToken = await attemptTokenRefresh();

      if (newToken) {
        isRefreshing = false;
        onTokenRefreshed(newToken);
        if (originalRequest.headers) {
          originalRequest.headers['x-access-token'] = newToken;
        }
        return axiosInstance(originalRequest);
      }

      // Refresh failed — clear session and redirect to login
      isRefreshing = false;
      refreshSubscribers = [];
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('refreshTokenTimestamp');
      localStorage.removeItem('userDetails');
      window.location.href = '/login';

      return Promise.reject(parseApiError(error));
    }

    return Promise.reject(parseApiError(error));
  },
);

// ─── Typed Request Methods ─────────────────────────────────────────────────

export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const response = await axiosInstance.get<T>(url, config);
  return { data: response.data, status: response.status };
}

export async function apiPost<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const response = await axiosInstance.post<T>(url, data, config);
  return { data: response.data, status: response.status };
}

export async function apiPut<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const response = await axiosInstance.put<T>(url, data, config);
  return { data: response.data, status: response.status };
}

export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const response = await axiosInstance.delete<T>(url, config);
  return { data: response.data, status: response.status };
}

/**
 * Creates an AbortController for request cancellation.
 * Pass the signal to any api method via config: { signal: controller.signal }
 */
export function createAbortController(): AbortController {
  return new AbortController();
}

export { parseApiError };
